/**
 * Send a reminder by hand (operator order 2026-10-06).
 *
 * Opens on one open request, shows exactly who will receive it (To and the
 * copy list the module always uses), takes an optional message that is
 * printed in the email above the request, and sends. The email is the same
 * themed reminder the scheduler sends; it lands on the Correspondence tab as
 * "Sent by hand" with the sender's name. The scheduled reminders keep their
 * own cadence.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;
const NOTE_MAX = 1000;

const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export default function ManualReminderDialog({ requestId, title, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/universe-requests/${encodeURIComponent(requestId)}/manual-reminder`, { credentials: 'include' })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Could not load the recipients.'); return d; })
      .then(setPreview)
      .catch((e) => setLoadError(e.message));
  }, [requestId]);

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/api/universe-requests/${encodeURIComponent(requestId)}/send-reminder`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'The reminder was not sent.');
      setDone(d);
      if (onSent) onSent(d);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const noRecipients = preview && (preview.to || []).length === 0;
  const overdue = preview?.due_at && new Date(preview.due_at) < new Date();

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Send a reminder">
      <div className="bg-white border border-slate-300 w-full max-w-xl mt-16 shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-mono uppercase tracking-widest text-red-700">Send a reminder</div>
            <div className="text-sm font-semibold text-slate-900 truncate">{title || 'Universe request'}</div>
          </div>
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-900">Close</button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {loadError && <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{loadError}</div>}
          {!preview && !loadError && <div className="text-xs text-slate-500">Loading recipients…</div>}

          {preview && !done && (
            <>
              <div className="text-xs text-slate-600 grid grid-cols-[70px_1fr] gap-y-1">
                <span className="font-mono uppercase text-slate-400">To</span>
                <span className="break-words">{noRecipients ? <span className="text-red-700">No recipient on file for this request.</span> : preview.to.join(', ')}</span>
                {(preview.cc || []).length > 0 && (<><span className="font-mono uppercase text-slate-400">Cc</span><span className="break-words">{preview.cc.join(', ')}</span></>)}
                {preview.subject && (<><span className="font-mono uppercase text-slate-400">Subject</span><span>{preview.subject}</span></>)}
                {preview.due_at && (<><span className="font-mono uppercase text-slate-400">Due</span><span className={overdue ? 'text-red-700 font-semibold' : ''}>{fmt(preview.due_at)}{overdue ? ' · overdue' : ''}</span></>)}
                {preview.last_manual_at && (<><span className="font-mono uppercase text-slate-400">Last</span><span>A reminder was last sent by hand on {fmt(preview.last_manual_at)}.</span></>)}
              </div>
              <div>
                <label htmlFor="ur-reminder-note" className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Message (optional)</label>
                <textarea id="ur-reminder-note" value={note} maxLength={NOTE_MAX} rows={4} onChange={(e) => setNote(e.target.value)}
                  placeholder="A short note printed in the email above the request, for example what is still missing."
                  className="w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-red-700" />
                <div className="flex justify-between text-[11px] text-slate-500">
                  <span>Do not include member information. The email already carries the request, the due date and the link.</span>
                  <span className="font-mono">{note.length}/{NOTE_MAX}</span>
                </div>
              </div>
              {error && <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </>
          )}

          {done && (
            <div className="border border-emerald-300 bg-emerald-50 text-emerald-900 px-3 py-2 text-sm">
              Reminder sent to {done.sent_count} recipient{done.sent_count === 1 ? '' : 's'}. It is on the Correspondence tab under this request as sent by hand.
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {done ? (
            <button onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-1.5 text-sm font-semibold">Done</button>
          ) : (
            <>
              <button onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
              <button onClick={send} disabled={busy || !preview || noRecipients}
                className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-1.5 text-sm font-semibold">
                {busy ? 'Sending…' : 'Send reminder now'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
